import { useState, type FC } from "react";
import { Button } from "../../components/ui/button";
import { Card } from "../../components/ui/card";
import { Label } from "../../components/ui/label";
import { Activity } from "lucide-react";
import { useNavigate } from "react-router-dom";
import GluceraNavigationRoutes from "../../utils/navigationRoutes";
import SessionStorageHelper from "../../utils/sessionStorageHelper";

const EatingWindowPage: FC = () => {
  const navigate = useNavigate();
  const [selected, setSelected] = useState<string>("");

  const options = [
    "16:8 (Fast 16 hrs / Eat 8 hrs)",
    "18:6 (Fast 18 hrs / Eat 6 hrs)",
    "14:10 (Fast 14 hrs / Eat 10 hrs)",
    "My eating window varies",
  ];

  const handleNext = () => {
    if (!selected) {
      return;
    }

    // Use Django choice keys (first value in tuple)
    SessionStorageHelper.saveMultipleFields({
      fasting_window_type: selected,
      intermittent_fasting: "Yes — I follow a fasting window",
    });

    navigate("/glucera" + GluceraNavigationRoutes.ASSESSMENT_SUMMARY);
  };

  return (
    <div className="h-screen w-screen overflow-hidden bg-background">
      <div className="mx-auto flex h-full w-full max-w-md flex-col">
        {/* Header */}
        <div className="space-y-4 p-4">
          {/* Logo */}
          <div className="flex items-center justify-center gap-2">
            <div className="flex h-10 w-10 items-center justify-center rounded-lg bg-gradient-to-br from-blue-500 to-orange-400">
              <Activity className="h-5 w-5 text-white" />
            </div>
            <span className="text-lg font-semibold text-slate-500">
              Bite Balance
            </span>
          </div>

          {/* Assessment Progress */}
          <p className="text-center text-sm text-muted-foreground">
            Assessment 11 of 12
          </p>
        </div>

        {/* Content */}
        <div className="flex flex-1 flex-col px-6 py-2">
          <div className="mb-6 space-y-2">
            <Label className="text-sm text-foreground">
              What's your typical eating window?
            </Label>
          </div>

          <div className="space-y-3">
            {options.map(option => (
              <Card
                key={option}
                onClick={() => setSelected(option)}
                className={`cursor-pointer p-3 transition-all hover:shadow-md ${selected === option
                  ? "border-primary bg-primary/5"
                  : "border-border hover:border-primary/50"
                  }`}
              >
                <div className="flex items-center gap-3">
                  <div
                    className={`flex h-5 w-5 items-center justify-center rounded-full border-2 ${selected === option
                      ? "border-primary bg-primary"
                      : "border-muted-foreground"
                      }`}
                  >
                    {selected === option && (
                      <div className="h-2 w-2 rounded-full bg-primary-foreground" />
                    )}
                  </div>
                  <span className="flex-1 text-sm text-foreground">
                    {option}
                  </span>
                </div>
              </Card>
            ))}
          </div>

          {/* Button Container */}
          <div className="mt-auto pt-4">
            <Button
              onClick={handleNext}
              disabled={!selected}
              className="h-11 w-full rounded-xl bg-gradient-primary text-sm hover:opacity-90 disabled:opacity-50 sm:h-12 sm:text-base"
            >
              {selected ? "Next" : "Select Eating Window"}
            </Button>
          </div>
        </div>

        {/* Safe area padding */}
        <div className="pb-safe p-2" />
      </div>
    </div>
  );
};

export default EatingWindowPage;
